/* eslint-disable react-dom/no-dangerously-set-innerhtml */
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
    faCircleInfo,
    faDrum,
    faMicrophoneLines,
    faRecordVinyl,
    faTowerBroadcast,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DOMPurify from 'dompurify'
import { useState } from 'react'
import ReactGA from 'react-ga4'
import { Parallax } from 'react-scroll-parallax'
import {
    aside,
    howWeWorkHeading,
    howWeWorkItems,
    processHeading,
    processes,
    services,
    servicesHeading,
} from '../data/services'
import { ImagePreloader } from './ImagePreloader'
import Masonry, { ResponsiveMasonry } from './Masonry'

const iconMap: Record<string, IconDefinition> = {
  faMicrophoneLines,
  faRecordVinyl,
  faDrum,
  faTowerBroadcast,
}

export default function Services() {
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {}
  )

  const toggleCard = (cardId: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }))
  }

  const className = 'z-0 h-auto w-full scale-100 rounded-xl'

  return (
    <section id="services" className="cv-auto py-16 sm:py-24">
      <div className="container-responsive">
        <div className="relative z-30 mb-8 max-w-md">
          {servicesHeading.heading && (
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {servicesHeading.heading}
            </h2>
          )}
          {servicesHeading.paragraph && (
            <p className="mt-3 text-neutral-600 dark:text-neutral-300">
              {servicesHeading.paragraph}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-12 md:grid-cols-12 lg:grid-cols-12">
          {/* Sidebar: USPs */}
          <aside className="z-30 sm:col-span-6 md:col-span-4 lg:col-span-3">
            <div className="sticky top-16 z-30 rounded-xl border border-neutral-200/80 bg-white/95 p-5 shadow-sm backdrop-blur-sm dark:border-neutral-700/50 dark:bg-neutral-900/95">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
                {aside.heading}
              </h3>
              <ul className="text-sm mt-4 space-y-3">
                {aside.items.map((item) => (
                  <li key={item.description} className="flex items-start gap-3">
                    <span
                      className="mt-0.5 text-amber-600 dark:text-amber-500"
                      aria-hidden
                    >
                      <FontAwesomeIcon icon={item.icon} />
                    </span>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.description),
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div className="top-100 sticky z-0 pt-5">
              <Parallax
                translateY={[-10, 3]}
                disabled={true}
                className="z-0 max-w-[970px] lg:max-w-[800px]"
              >
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/media/strangeways-aerial_320.webp 320w,
                     /media/strangeways-aerial_480.webp 480w,
                     /media/strangeways-aerial_640.webp 640w,
                     /media/strangeways-aerial_768.webp 768w,
                     /media/strangeways-aerial_960.webp 960w,
                     /media/strangeways-aerial_1200.webp 1200w"
                    sizes="100vw"
                  />
                  <ImagePreloader
                    src="/media/strangeways-aerial_320.webp"
                    alt="Strangeways Studios aerial view"
                    className={className}
                    loading="lazy"
                    decoding="async"
                    width={320}
                    height={427}
                  />
                </picture>
              </Parallax>
            </div>
          </aside>

          {/* Service cards */}
          <div className="sm:col-span-6 md:col-span-8 lg:col-span-9">
            <ResponsiveMasonry
              {...{
                columnsCountBreakPoints: {
                  320: 1,
                  768: 2,
                  960: 2,
                },
                className: 'relative z-20',
                gutterBreakPoints: {
                  320: '1rem',
                  768: '1rem',
                  960: '1rem',
                },
              }}
            >
              <Masonry>
                {services.map((service) => (
                  <article
                    key={service.id}
                    className="group"
                    data-aos="fade-up-left"
                    onClick={(e) => {
                      e.preventDefault()
                      toggleCard(service.id)
                      ReactGA.event({
                        category: 'Button Click',
                        action: `User clicked to expand: ${service.title}`,
                        label: 'Services Section',
                      })
                    }}
                  >
                    <div
                      className={`relative z-30 mb-0 h-fit cursor-pointer rounded-xl border p-5 transition-all duration-300 ${expandedCards[service.id] ? 'border-amber-300 bg-white shadow-lg dark:border-amber-600/50 dark:bg-neutral-900' : 'border-neutral-200/60 bg-white/90 shadow-sm backdrop-blur-sm hover:border-neutral-300 hover:shadow-md dark:border-neutral-700/50 dark:bg-neutral-900/90 dark:hover:border-neutral-600'}`}
                    >
                      <div
                        className={`transition-all duration-300 ${expandedCards[service.id] ? 'h-0 overflow-hidden opacity-0' : 'opacity-100'}`}
                      >
                        <div className="absolute right-2 top-1">
                          <FontAwesomeIcon
                            icon={faCircleInfo}
                            size="1x"
                            className="text-neutral-600 opacity-50 transition-opacity duration-300 ease-in-out group-hover:opacity-100 dark:text-neutral-400"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className="text-amber-600 dark:text-amber-500"
                            aria-hidden
                          >
                            <FontAwesomeIcon
                              icon={iconMap[service.icon]}
                              size="xl"
                            />
                          </span>
                          <h3 className="text-2xl font-semibold leading-tight text-neutral-900 dark:text-neutral-100">
                            {service.title}
                          </h3>
                        </div>
                        <p className="text-base mt-3 leading-relaxed text-neutral-600 dark:text-neutral-400">
                          {service.description}
                        </p>
                      </div>
                      <ul
                        className={`text-sm list-inside list-disc leading-relaxed text-neutral-700 transition-all duration-300 dark:text-neutral-300 ${expandedCards[service.id] ? 'opacity-100' : 'h-0 overflow-hidden opacity-0'}`}
                      >
                        {service.details.map((detail) => (
                          <li
                            className="mb-2"
                            key={detail}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(detail),
                            }}
                          />
                        ))}
                      </ul>
                    </div>
                  </article>
                ))}
              </Masonry>
            </ResponsiveMasonry>

            {/* How We Work */}
            <div
              id="how-we-work"
              className="relative z-20 mt-6 rounded-xl border border-neutral-200/60 bg-white/90 p-6 shadow-sm backdrop-blur-sm dark:border-neutral-700/50 dark:bg-neutral-900/90"
              data-aos="fade-up-left"
            >
              {howWeWorkHeading.heading && (
                <h3 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                  {howWeWorkHeading.heading}
                </h3>
              )}
              {howWeWorkHeading.paragraph && (
                <p className="text-sm mt-2 leading-relaxed text-neutral-600 dark:text-neutral-400">
                  {howWeWorkHeading.paragraph}
                </p>
              )}
              <div className="mt-4">
                <ResponsiveMasonry
                  {...{
                    columnsCountBreakPoints: {
                      320: 1,
                      768: 3,
                      960: 3,
                    },
                    className: 'relative z-20',
                    gutterBreakPoints: {
                      320: '1rem',
                      768: '1rem',
                      960: '1rem',
                    },
                  }}
                >
                  <Masonry>
                    {howWeWorkItems.map((item) => (
                      <article
                        key={item.id}
                        className={`group relative cursor-pointer rounded-lg border p-4 transition-all duration-300 ${expandedCards[item.id] ? 'border-amber-300 bg-amber-50/50 dark:border-amber-600/50 dark:bg-amber-900/10' : 'border-neutral-200 bg-neutral-50/80 hover:border-neutral-300 hover:bg-neutral-100/80 dark:border-neutral-700 dark:bg-neutral-800/50 dark:hover:border-neutral-600 dark:hover:bg-neutral-800/80'}`}
                        onClick={(e) => {
                          e.preventDefault()
                          toggleCard(item.id)
                          ReactGA.event({
                            category: 'Button Click',
                            action: `User clicked to expand: ${item.title}`,
                            label: 'How We Work Section',
                          })
                        }}
                      >
                        <div className="mb-0">
                          <div
                            className={`transition-all duration-300 ${expandedCards[item.id] ? 'h-0 overflow-hidden opacity-0' : 'opacity-100'}`}
                          >
                            <div className="absolute right-2 top-1">
                              <FontAwesomeIcon
                                icon={faCircleInfo}
                                size="1x"
                                className="text-neutral-600 opacity-50 transition-opacity duration-300 ease-in-out group-hover:opacity-100 dark:text-neutral-400"
                              />
                            </div>
                            <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                              {item.title}
                            </h4>
                            <p className="text-sm mt-2 text-neutral-600 dark:text-neutral-400">
                              {item.summary}
                            </p>
                          </div>
                          <ul
                            className={`text-sm list-inside list-disc leading-relaxed text-neutral-700 transition-all duration-300 dark:text-neutral-300 ${expandedCards[item.id] ? 'opacity-100' : 'h-0 overflow-hidden opacity-0'}`}
                          >
                            {item.details.map((detail) => (
                              <li
                                className="mb-2"
                                key={detail}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(detail),
                                }}
                              />
                            ))}
                          </ul>
                        </div>
                      </article>
                    ))}
                  </Masonry>
                </ResponsiveMasonry>
              </div>
            </div>

            {/* Process */}
            <div
              className="relative z-20 mt-6 rounded-xl border border-neutral-200/60 bg-white/90 p-6 shadow-sm backdrop-blur-sm dark:border-neutral-700/50 dark:bg-neutral-900/90"
              data-aos="fade-up-left"
            >
              <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-400">
                {processHeading}
              </h3>

              <ol
                className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-4"
                style={{ counterReset: 'step' }}
              >
                {processes.map((item) => (
                  <li
                    key={item.heading}
                    className="before:text-sm relative pl-6 before:absolute before:left-0 before:top-0 before:font-bold before:text-amber-600 before:content-[counter(step)] dark:before:text-amber-500"
                    style={{ counterIncrement: 'step' }}
                  >
                    <strong className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                      {item.heading}
                    </strong>
                    <p
                      className="text-sm mt-1 leading-relaxed text-neutral-600 dark:text-neutral-400"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.paragraph),
                      }}
                    />
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
